/* eslint-disable react/jsx-one-expression-per-line */
import Button from 'common/components/Button'
import Checkbox from 'common/components/Checkbox'
import Dropdown from 'common/components/Dropdown'
import Flex from 'common/components/Flex'
import InputTextArea from 'common/components/TextArea'
import Input from 'common/components/TextInput'
import Toast from 'common/components/Toast'
import Typography from 'common/components/Typography'
import {
  GRECAPTCHA_SITE_KEY,
  WEB_DESKTOP_RALALI_URL,
} from 'common/constants'
import theme from 'common/theme'
import isEmail from 'lib/common/is-email'
import isEmpty from 'lib/common/is-empty'
import isNumber from 'lib/common/is-number'
import MediaHelper from 'lib/media-helper'
import Script from 'next/script'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useStoreActions } from 'stores/hooks'
import styled from 'styled-components'

const WrapperInput = styled.div`
  display: flex;
  flex-direction: column;
  width: ${({ width }: { width: string }) => width};

  ${MediaHelper('mobile')`width: 100%;`}
`

const TextInput = styled(Input)`
  width: 100%;
  height: 40px;
`

const TextArea = styled(InputTextArea)`
  width: 100%;
  height: 130px;
`

const TitleContactForm = styled(Typography)`
  text-align: center;
  margin-bottom: 24px;
  font-size: 32px;
  font-weight: bold;

  ${MediaHelper(
    'mobile',
  )`font-size: 20px; margin-bottom: 15px; font-weight: bold;`}
`

const StyledLinkText = styled.a`
  text-decoration: none;
  color: ${theme.colors.orange};
  ${(props) => (props.bold ? 'font-weight: bold;' : '')}
`

declare global {
  interface Window {
    grecaptcha: ReCaptchaInstance
  }
}

interface ReCaptchaExecuteOptions {
  action: string
}

interface ReCaptchaInstance {
  ready: (cb: () => any) => void
  execute: (
    key: string,
    options: ReCaptchaExecuteOptions,
  ) => Promise<string>
}

interface IContactUsProps {
  inputWidth?: string
}

function ContactUsForm({
  inputWidth = '460px',
}: IContactUsProps): JSX.Element {
  const [inputMargin, setInputMargin] = useState<string>('0 0 15px')

  const [name, setInpName] = useState<string>('')
  const [email, setInpEmail] = useState<string>('')
  const [handphone, setInpHandphone] = useState<string>('')
  const [company, setInpCompany] = useState<string>('')
  const [need, setInpNeeds] = useState<string>('')
  const [words, setInpWords] = useState<string>('')
  const [agreement, setAgreement] = useState<boolean>(false)

  const [nameHelper, setNameHelper] = useState<string>('')
  const [emailHelper, setEmailHelper] = useState<string>('')
  const [handphoneHelper, setHandphoneHelper] = useState<string>('')
  const [companyHelper, setCompanyHelper] = useState<string>('')
  const [needHelper, setNeedHelper] = useState<string>('')
  const [wordsHelper, setWordsHelper] = useState<string>('')

  const [notifMsg, setNotifMsg] = useState<string>('')
  const [isValidated, setIsValidated] = useState<boolean>(false)
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false)
  const [isFormSubmitted, setFormSubmitted] = useState(false)

  const { t } = useTranslation(['common'])

  const translateHelper = useCallback(
    (key, field) => {
      return t(key, {
        field: t(field),
      })
    },
    [t],
  )

  const { verifyCaptcha } = useStoreActions(
    (actions) => actions.businessSolution,
  )

  const pilihanKebutuhan = [
    // 'Ralalifood',
    'Ralali Pavilions',
    'Ralali Tender',
    'Ralali Direct',
    'Ralali Agent',
    'Ralali Plus',
  ]

  const renderKebutuhan = (items) => {
    return items.map((item, index) => (
      <option key={`pilihan-${String(index)}`} value={item}>
        {item}
      </option>
    ))
  }

  const checkInputValidated = () => {
    return (
      name &&
      email &&
      isEmail(email) &&
      handphone &&
      isNumber(handphone) &&
      company &&
      need &&
      words &&
      agreement
    )
  }

  const validateName = (value) => {
    if (!isEmpty(value)) {
      return setNameHelper('')
    }
    return setNameHelper(
      translateHelper('FIELD_IS_REQUIRED', 'FULL_NAME'),
    )
  }

  const validateEmail = (value) => {
    if (isEmpty(value)) {
      return setEmailHelper(
        translateHelper('FIELD_IS_REQUIRED', 'EMAIL'),
      )
    }

    if (!isEmail(value)) {
      return setEmailHelper(
        translateHelper('INVALID_FIELD_FORMAT', 'EMAIL'),
      )
    }

    return setEmailHelper('')
  }

  const validateHandphone = (value) => {
    if (isEmpty(value)) {
      return setHandphoneHelper(
        translateHelper('FIELD_IS_REQUIRED', 'PHONE_NUMBER'),
      )
    }

    if (!isNumber(value)) {
      return setHandphoneHelper(
        translateHelper('INVALID_FIELD_FORMAT', 'PHONE_NUMBER'),
      )
    }

    return setHandphoneHelper('')
  }

  const validateCompany = (value) => {
    if (!isEmpty(value)) {
      return setCompanyHelper('')
    }

    return setCompanyHelper(
      translateHelper('FIELD_IS_REQUIRED', 'COMPANY_NAME'),
    )
  }

  const validateNeeds = (value) => {
    if (!isEmpty(value)) {
      return setNeedHelper('')
    }

    return setNeedHelper(
      translateHelper('FIELD_IS_REQUIRED', 'NEEDS'),
    )
  }

  const validateWords = (value) => {
    if (!isEmpty(value)) {
      return setWordsHelper('')
    }

    return setWordsHelper(
      translateHelper('FIELD_IS_REQUIRED', 'YOUR_MESSAGE'),
    )
  }

  const resetForm = () => {
    setIsLoadingSubmit(false)
    setNotifMsg(
      t('SUCCESS_SUBMIT_CONTACT_US_FORM', { ns: 'businessSolution' }),
    )
    setFormSubmitted(false)
    setIsValidated(false)
    setInputMargin('0 0 15px')

    setInpName('')
    setInpEmail('')
    setInpHandphone('')
    setInpCompany('')
    setInpNeeds('')
    setInpWords('')
    setAgreement(false)

    setNameHelper('')
    setEmailHelper('')
    setHandphoneHelper('')
    setCompanyHelper('')
    setNeedHelper('')
    setWordsHelper('')
  }

  const onSubmit = async () => {
    setFormSubmitted(true)
    setInputMargin('0')
    setNotifMsg('')

    if (checkInputValidated()) {
      setIsLoadingSubmit(true)

      window.grecaptcha.ready(() => {
        window.grecaptcha
          .execute(GRECAPTCHA_SITE_KEY, { action: 'submit' })
          .then(async (token) => {
            const resStatus = await verifyCaptcha({
              token,
              data: {
                name,
                email,
                phone: handphone,
                companyName: company,
                kebutuhanPerihal: need,
                message: words,
              },
            })

            if (resStatus === 200) {
              resetForm()
            }

            setIsLoadingSubmit(false)
          })
          .catch(() => {
            setIsLoadingSubmit(false)

            Toast(
              'Terjadi Kesalahan, Mohon Coba Beberapa Saat Lagi.',
              {
                type: 'error',
                theme: 'colored',
                hideProgressBar: false,
                autoClose: 5000,
                pauseOnHover: true,
                position: 'top-center',
                toastId: 'error-bss-contact-us-form',
              },
            )
          })
      })
    } else {
      validateName(name)
      validateEmail(email)
      validateHandphone(handphone)
      validateCompany(company)
      validateNeeds(need)
      validateWords(words)
    }
  }

  const handleChangeName = (e) => {
    validateName(e.target.value)
    setInpName(e.target.value)
  }

  const handleChangeEmail = (e) => {
    validateEmail(e.target.value)
    setInpEmail(e.target.value)
  }

  const handleChangeHandphone = (e) => {
    validateHandphone(e.target.value)
    setInpHandphone(e.target.value)
  }

  const handleChangeCompany = (e) => {
    validateCompany(e.target.value)
    setInpCompany(e.target.value)
  }

  const handleChangeWords = (e) => {
    validateWords(e.target.value)
    setInpWords(e.target.value)
  }

  const agreementHandler = () => {
    setAgreement(!agreement)
  }

  const handleChangeNeeds = (val) => {
    setInpNeeds(val)
  }

  useEffect(() => {
    const isValid = checkInputValidated()
    setIsValidated(isValid)
  }, [name, email, handphone, company, need, words, agreement])

  useEffect(() => {
    if (isFormSubmitted) {
      validateNeeds(need)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [need])

  return (
    <>
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${GRECAPTCHA_SITE_KEY}`}
      />
      <TitleContactForm>{t('CONTACT_US')}!</TitleContactForm>

      <WrapperInput width={inputWidth}>
        <Typography marginY="5px" type="blabel">
          {t('FULL_NAME')} *
        </Typography>
        <TextInput
          hideHelperText={!isFormSubmitted}
          margin={inputMargin}
          placeholder={t('YOUR_FULL_NAME')}
          fontSize="13px"
          padding="11px 13px"
          value={name}
          data-testid="nama-test"
          onChange={handleChangeName}
          helperText={nameHelper}
          error={!!nameHelper}
        />
      </WrapperInput>

      <WrapperInput width={inputWidth}>
        <Typography marginY="5px" type="blabel">
          {t('EMAIL')} *
        </Typography>

        <TextInput
          type="email"
          hideHelperText={!isFormSubmitted}
          margin={inputMargin}
          placeholder={t('YOUR_EMAIL')}
          fontSize="13px"
          padding="11px 13px"
          value={email}
          data-testid="email-test"
          onChange={handleChangeEmail}
          helperText={emailHelper}
          error={!!emailHelper}
        />
      </WrapperInput>

      <WrapperInput width={inputWidth}>
        <Typography marginY="5px" type="blabel">
          {t('PHONE_NUMBER')} *
        </Typography>

        <TextInput
          hideHelperText={!isFormSubmitted}
          margin={inputMargin}
          placeholder={t('YOUR_PHONE_NUMBER')}
          fontSize="13px"
          padding="11px 13px"
          value={handphone}
          data-testid="handphone-test"
          onChange={handleChangeHandphone}
          helperText={handphoneHelper}
          error={!!handphoneHelper}
        />
      </WrapperInput>

      <WrapperInput width={inputWidth}>
        <Typography marginY="5px" type="blabel">
          {t('COMPANY_NAME')} *
        </Typography>

        <TextInput
          hideHelperText={!isFormSubmitted}
          margin={inputMargin}
          placeholder={t('YOUR_COMPANY_NAME')}
          fontSize="13px"
          padding="11px 13px"
          value={company}
          data-testid="company-test"
          onChange={handleChangeCompany}
          helperText={companyHelper}
          error={!!companyHelper}
        />
      </WrapperInput>

      <WrapperInput width={inputWidth}>
        <Typography marginY="5px" type="blabel">
          {t('NEEDS')} *
        </Typography>

        <Dropdown
          width={inputWidth}
          height="40px"
          id="need"
          margin={needHelper ? '0' : '0 0 15px'}
          placeholder={t('CHOOSE_YOUR_NEEDS')}
          value={need}
          onChange={handleChangeNeeds}
          helperText={needHelper}
          error={!!needHelper}
        >
          {renderKebutuhan(pilihanKebutuhan)}
        </Dropdown>
      </WrapperInput>

      <WrapperInput width={inputWidth}>
        <Typography marginY="5px" type="blabel">
          {t('YOUR_MESSAGE')} *
        </Typography>

        <TextArea
          hideHelperText={!isFormSubmitted}
          margin={inputMargin}
          placeholder={t('WRITE_YOUR_MESSAGE')}
          resize="none"
          fontSize="13px"
          padding="11px 13px"
          value={words}
          data-testid="kata-test"
          onChange={handleChangeWords}
          helperText={wordsHelper}
          error={!!wordsHelper}
        />
      </WrapperInput>

      <WrapperInput width={inputWidth}>
        <Flex>
          <>
            <Checkbox
              onClick={agreementHandler}
              data-testid="checkbox-agreement"
              checked={agreement}
            />
            <Typography marginLeft="10px">
              <>
                {t('FORM_AGREE')}
                <StyledLinkText
                  href={`${WEB_DESKTOP_RALALI_URL}/syaratdanketentuan`}
                  target="_blank"
                >
                  {` ${t('TERMS_CONDITION', { ns: 'auth' })} `}
                </StyledLinkText>
                {t('AND', { ns: 'auth' })}
                <StyledLinkText
                  href={`${WEB_DESKTOP_RALALI_URL}/kebijakanprivasi`}
                  target="_blank"
                >
                  {` ${t('PRIVACY_POLICY', { ns: 'auth' })}`}
                </StyledLinkText>
              </>
            </Typography>
          </>
        </Flex>
      </WrapperInput>

      <Button
        width="fit-content"
        margin="19px 0 0"
        padding="25px 73px"
        color="redOrange"
        borderRadius="8px"
        data-testid="submit-test"
        disabled={!isValidated}
        onClick={onSubmit}
        isLoading={isLoadingSubmit}
      >
        <Typography fontSize="16px" fontWeight="bold" color="white">
          {t('SEND')}
        </Typography>
      </Button>
      <Typography textAlign="center" type="rlead" marginTop="7px">
        {notifMsg}
      </Typography>
    </>
  )
}

export default ContactUsForm
